import { useMemo } from 'react';
import { filterCategoriesEligibleForNewForm } from 'src/components/pages/FormCreation/components/CategorySelection/helpers/filterCategoriesEligibleForNewForm';
import { CategoryType, useGetOrganisationCategoriesQuery } from 'src/graphql';
import type { FormManagementSection } from './types';

interface UseFormManagementCategoryEligibilityArgs {
  speakUpEnabled: boolean;
  coiEnabled: boolean;
  selectedProductTab: FormManagementSection;
  isTableLoading: boolean;
  noCategoriesBannerDismissed: boolean;
}

export function useFormManagementCategoryEligibility({
  speakUpEnabled,
  coiEnabled,
  selectedProductTab,
  isTableLoading,
  noCategoriesBannerDismissed,
}: UseFormManagementCategoryEligibilityArgs) {
  const {
    data: organisationCategoriesData,
    isLoading: isSpeakUpCategoriesLoading,
    isError: isSpeakUpCategoriesError,
    refetch: refetchSpeakUpCategories,
  } = useGetOrganisationCategoriesQuery(
    { isCOI: false },
    {
      enabled: speakUpEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: coiOrganisationCategoriesData,
    isLoading: isCoiCategoriesLoading,
    isError: isCoiCategoriesError,
    refetch: refetchCoiCategories,
  } = useGetOrganisationCategoriesQuery(
    { isCOI: true },
    {
      enabled: coiEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const speakUpOrgCategoriesEligibleForForms = useMemo(() => {
    return filterCategoriesEligibleForNewForm(
      organisationCategoriesData?.getOrganisationCategories as
        | CategoryType[]
        | null
        | undefined,
      false,
    );
  }, [organisationCategoriesData]);

  const coiOrgCategoriesEligibleForForms = useMemo(() => {
    return filterCategoriesEligibleForNewForm(
      coiOrganisationCategoriesData?.getOrganisationCategories as
        | CategoryType[]
        | null
        | undefined,
      true,
    );
  }, [coiOrganisationCategoriesData]);

  const canCreateForm = speakUpEnabled || coiEnabled;

  // data is undefined during both loading and error, so without this a
  // failed request would look the same as "no categories".
  const isCategoriesUnsettledForEnabledProducts =
    (speakUpEnabled &&
      (isSpeakUpCategoriesLoading || isSpeakUpCategoriesError)) ||
    (coiEnabled && (isCoiCategoriesLoading || isCoiCategoriesError));

  const hasCategoriesForAnyEnabledProduct =
    !isCategoriesUnsettledForEnabledProducts &&
    ((speakUpEnabled && speakUpOrgCategoriesEligibleForForms.length > 0) ||
      (coiEnabled && coiOrgCategoriesEligibleForForms.length > 0));

  const hasEligibleCategoriesForNewForm =
    !isCategoriesUnsettledForEnabledProducts &&
    ((speakUpEnabled && speakUpOrgCategoriesEligibleForForms.length > 0) ||
      (coiEnabled && coiOrgCategoriesEligibleForForms.length > 0));

  const createFormDisabledDueToCategories =
    canCreateForm &&
    !isCategoriesUnsettledForEnabledProducts &&
    !hasEligibleCategoriesForNewForm;
  const showCreateFormDropdown =
    speakUpEnabled && coiEnabled && !createFormDisabledDueToCategories;

  const isSelectedTabCategoriesLoading =
    selectedProductTab === 'COI'
      ? isCoiCategoriesLoading
      : isSpeakUpCategoriesLoading;

  const isSelectedTabCategoriesError =
    selectedProductTab === 'COI'
      ? isCoiCategoriesError
      : isSpeakUpCategoriesError;

  const refetchSelectedTabCategories =
    selectedProductTab === 'COI'
      ? refetchCoiCategories
      : refetchSpeakUpCategories;

  const hasAnyOrgCategoriesForSelectedTab =
    selectedProductTab === 'COI'
      ? (coiOrganisationCategoriesData?.getOrganisationCategories?.length ??
          0) > 0
      : (organisationCategoriesData?.getOrganisationCategories?.length ?? 0) >
        0;

  const hasEligibleCategoriesForSelectedTab =
    selectedProductTab === 'COI'
      ? coiOrgCategoriesEligibleForForms.length > 0
      : speakUpOrgCategoriesEligibleForForms.length > 0;

  const showNoCategoriesBanner =
    !noCategoriesBannerDismissed &&
    !isTableLoading &&
    !isSelectedTabCategoriesLoading &&
    !isSelectedTabCategoriesError &&
    canCreateForm &&
    hasAnyOrgCategoriesForSelectedTab &&
    !hasEligibleCategoriesForSelectedTab;

  return {
    organisationCategoriesData,
    coiOrganisationCategoriesData,
    speakUpOrgCategoriesEligibleForForms,
    coiOrgCategoriesEligibleForForms,
    canCreateForm,
    isCategoriesUnsettledForEnabledProducts,
    hasCategoriesForAnyEnabledProduct,
    hasEligibleCategoriesForNewForm,
    createFormDisabledDueToCategories,
    showCreateFormDropdown,
    showNoCategoriesBanner,
    isSelectedTabCategoriesLoading,
    isSelectedTabCategoriesError,
    refetchSelectedTabCategories,
  };
}
